/**
 * 🆕 2026-02-21 Phase 8 — Modale générique réutilisable pour les critères CP.
 *
 * Spec utilisateur Message 2 §9 :
 *   « Chaque critère CP doit être cliquable et ouvrir un détail exploitable avec :
 *     nom du client, chef d'équipe, date de production, statut conforme/non,
 *     raison du non-respect, action possible (lien vers le dossier). »
 *
 * Choix UX :
 *   - 1 seule modale, colonnes dynamiques selon `critereKey`
 *   - Toggle "Tout / Non conforme uniquement" (par défaut : non conforme uniquement)
 *   - Lien "Voir le dossier" qui ouvre /dossiers/{dossier_id} dans un nouvel onglet
 *   - Pas de boutons d'action avancés pour cette livraison (Phase 8 stricte)
 */
import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, CheckCircle, AlertCircle, Filter } from "lucide-react";

const CRITERE_CONFIG = {
  objectif_temps: {
    title: "Objectif temps des équipes — Détail",
    subtitle: "BT terminés avec heures budgétées (saison sélectionnée)",
    columns: ["Client", "Chef", "Date prod.", "Budget", "Réel", "Écart", "Statut"],
    renderRow: (d) => [
      d.client_nom || "—",
      d.chef_nom || "—",
      d.date_production || "—",
      `${d.heures_budget ?? 0} h`,
      `${d.heures_reelles ?? 0} h`,
      d.ecart_heures != null ? `${d.ecart_heures > 0 ? "+" : ""}${d.ecart_heures} h` : "—",
    ],
  },
  photos: {
    title: "Photos avant/après — Détail",
    subtitle: "Dossiers où les photos sont requises",
    columns: ["Client", "Chef", "Date prod.", "Statut"],
    renderRow: (d) => [
      d.client_nom || "—",
      d.chef_nom || "—",
      d.date_production || "—",
    ],
  },
  inspection_cp: {
    title: "Inspections de chantier — Détail",
    subtitle: "BT de la saison et leurs inspections",
    columns: ["Client", "Chef", "Date prod.", "Type", "Nb inspect.", "Statut"],
    renderRow: (d) => [
      d.client_nom || "—",
      d.chef_nom || "—",
      d.date_production || "—",
      d.type_travaux || "—",
      d.nb_inspections ?? 0,
    ],
  },
  completude_bt: {
    title: "Bons de travail complets — Détail",
    subtitle: "BT de la saison et complétude des informations",
    columns: ["Client", "Chef", "Date prod.", "Type", "Élément(s) manquant(s)", "Statut"],
    renderRow: (d) => [
      d.client_nom || "—",
      d.chef_nom || "—",
      d.date_production || "—",
      d.type_travaux || "—",
      (d.issues && d.issues.length > 0) ? d.issues.join(", ") : "—",
    ],
  },
  lavage_72h: {
    title: "Lavage à pression 72 h avant — Détail",
    subtitle: "Routes de lavage terminées et délai avant travaux",
    columns: ["Client", "Chef", "Date lavage", "Début travaux", "Délai", "Statut"],
    renderRow: (d) => [
      d.client_nom || "—",
      d.chef_nom || "—",
      d.date_lavage || "—",
      d.date_debut_travaux || "—",
      d.delta_jours != null ? `${d.delta_jours} j` : "—",
    ],
  },
  email_presentation: {
    title: "Courriel de présentation — Détail",
    subtitle: "BT en production de la saison",
    columns: ["Client", "Chef", "Date prod.", "Statut"],
    renderRow: (d) => [
      d.client_nom || "—",
      d.chef_nom || "—",
      d.date_production || "—",
    ],
  },
};

export function CpCritereDetailsDialog({ open, onOpenChange, critereKey, critereData }) {
  const [showAll, setShowAll] = useState(false);
  const config = CRITERE_CONFIG[critereKey];

  const details = useMemo(() => critereData?.details || [], [critereData]);
  const nonConformes = useMemo(() => details.filter((d) => !d.ok), [details]);
  const visibleDetails = showAll ? details : nonConformes;

  if (!config) return null;

  const score = critereData?.score;
  const seuil = critereData?.seuil;
  const total = critereData?.total ?? details.length;
  const conformesCount = details.length - nonConformes.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="cp-critere-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            {config.title}
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1">{config.subtitle}</div>
        </DialogHeader>

        {/* Résumé */}
        <div className="flex items-center gap-3 flex-wrap text-sm bg-slate-50 rounded-lg p-3 border border-slate-200">
          <div>
            <span className="text-slate-500">Score : </span>
            <span className={`font-bold ${score == null ? "text-slate-400" : score >= (seuil || 0) ? "text-emerald-600" : "text-amber-600"}`}>
              {score == null ? "—" : `${score}%`}
            </span>
            {seuil != null && <span className="text-slate-400 text-xs"> / min {seuil}%</span>}
          </div>
          <span className="text-slate-300">|</span>
          <div>
            <span className="text-emerald-600 font-semibold">{conformesCount}</span>
            <span className="text-slate-500"> conformes</span>
          </div>
          <div>
            <span className="text-red-600 font-semibold">{nonConformes.length}</span>
            <span className="text-slate-500"> à corriger</span>
          </div>
          <div>
            <span className="text-slate-700 font-semibold">{total}</span>
            <span className="text-slate-500"> au total</span>
          </div>

          <div className="ml-auto">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowAll((v) => !v)}
              className="h-7 text-xs gap-1"
              data-testid="cp-critere-toggle-filter"
            >
              <Filter className="w-3 h-3" />
              {showAll ? "Voir seulement les à corriger" : "Tout afficher"}
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto border rounded-lg">
          {visibleDetails.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              {nonConformes.length === 0 && !showAll
                ? "🎉 Tous les éléments sont conformes — aucune action requise."
                : "Aucune donnée à afficher."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b sticky top-0 z-10">
                <tr>
                  {config.columns.map((c, i) => (
                    <th key={i} className="px-3 py-2 text-left font-semibold text-slate-700 text-xs">
                      {c}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left font-semibold text-slate-700 text-xs w-32">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleDetails.map((d, i) => {
                  const cells = config.renderRow(d);
                  return (
                    <tr key={i} className={`border-b hover:bg-slate-50 ${!d.ok ? "bg-amber-50/40" : ""}`}>
                      {cells.map((v, j) => (
                        <td key={j} className="px-3 py-2 text-slate-700">{v}</td>
                      ))}
                      <td className="px-3 py-2">
                        {d.ok ? (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 gap-1">
                            <CheckCircle className="w-3 h-3" /> Conforme
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50 gap-1">
                            <AlertCircle className="w-3 h-3" /> À corriger
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {d.dossier_id ? (
                          <a
                            href={`/dossiers/${d.dossier_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                            data-testid={`cp-critere-link-dossier-${i}`}
                          >
                            Voir le dossier <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Raisons explicites pour les non conformes */}
        {visibleDetails.some((d) => !d.ok && d.raison) && (
          <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded p-2 max-h-32 overflow-auto">
            <div className="font-semibold text-amber-800 mb-1">Détail des raisons :</div>
            <ul className="space-y-0.5">
              {visibleDetails
                .filter((d) => !d.ok && d.raison)
                .map((d, i) => (
                  <li key={i}>
                    <span className="font-medium">{d.client_nom || "—"}</span> : {d.raison}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CpCritereDetailsDialog;
